import { useCallback, useEffect, useRef, useState, type MutableRefObject } from "react";
import * as api from "../api/disciplio-api";
import { getTodayKey } from "../disciplio/logic";
import { saveState } from "../disciplio/storage";
import type { AppState, DisciplioSettings, UserProfile } from "../disciplio/types";
import type { Action } from "./disciplio-context-state";

type Dispatch = (action: Action) => void;

export function usePersistedDisciplioState(state: AppState, allowOptionalStorage: boolean) {
  useEffect(() => {
    saveState(state, allowOptionalStorage);
  }, [allowOptionalStorage, state]);
}

export function usePlanLoader(dispatch: Dispatch, pendingDatesRef: MutableRefObject<Set<string>>) {
  const loadedDateKeysRef = useRef<Set<string>>(new Set());
  const inFlightLoadsRef = useRef<Map<string, Promise<void>>>(new Map());

  return useCallback(
    async (dateKey: string, force = false) => {
      if (!api.hasApiSession()) return;
      if (pendingDatesRef.current.has(dateKey)) return;
      if (!force && loadedDateKeysRef.current.has(dateKey)) return;
      const active = inFlightLoadsRef.current.get(dateKey);
      if (active) {
        await active;
        return;
      }
      const load = api
        .getDailyPlan(dateKey)
        .then((items) => {
          dispatch({ type: "replaceDailyItems", dateKey, items });
          loadedDateKeysRef.current.add(dateKey);
        })
        .catch(() => undefined)
        .finally(() => {
          inFlightLoadsRef.current.delete(dateKey);
        });
      inFlightLoadsRef.current.set(dateKey, load);
      await load;
    },
    [dispatch, pendingDatesRef]
  );
}

export function useRemoteStateSynchronizer(
  dispatch: Dispatch,
  initialProfile: UserProfile,
  initialSettings: DisciplioSettings,
  markHydrated: (hydrated: boolean) => void,
  pendingDatesRef: MutableRefObject<Set<string>>,
  monthlyGoalMutationRevisionRef: MutableRefObject<number>,
  remoteRefreshBlockedRef: MutableRefObject<boolean>,
  sessionActive: boolean,
  sessionReady: boolean
) {
  const refreshInFlightRef = useRef<Promise<boolean> | null>(null);
  const [refreshRevision, setRefreshRevision] = useState(0);
  const [remoteLoadError, setRemoteLoadError] = useState(false);

  const loadRemoteState = useCallback(async (reportInitialFailure: boolean): Promise<boolean> => {
    if (!api.hasApiSession()) return false;
    if (refreshInFlightRef.current) return refreshInFlightRef.current;
    const refresh = (async () => {
      try {
        const monthlyGoalRevision = monthlyGoalMutationRevisionRef.current;
        const user = await api.getCurrentUser();
        const settings = api.settingsFromApiUser(user, initialSettings);
        const todayKey = getTodayKey(settings);
        const profile = api.profileFromApiUser(user, initialProfile);
        const [dailyItems, completionHistory, monthlyPlans, weeklyPlans, goals] = await Promise.all([
          pendingDatesRef.current.has(todayKey) ? Promise.resolve(null) : api.getDailyPlan(todayKey),
          api.getCompletionHistory(730, settings),
          api.listMonthlyPlans(),
          api.listWeeklyPlans(),
          api.listGoals(),
        ]);
        dispatch({ type: "hydrateProfile", profile: api.profileWithGoalsIndex(profile, goals) });
        dispatch({ type: "saveSettings", settings });
        if (dailyItems && !remoteRefreshBlockedRef.current) {
          dispatch({ type: "replaceDailyItems", dateKey: todayKey, items: dailyItems });
        }
        dispatch({ type: "replaceCompletionHistory", history: completionHistory });
        if (monthlyGoalMutationRevisionRef.current === monthlyGoalRevision) {
          dispatch({ type: "replaceMonthlyGoals", goals: api.attachYearGoalTitles(api.currentMonthGoals(monthlyPlans), goals) });
        }
        dispatch({ type: "replaceWeeklyGoals", goals: api.currentWeekGoals(weeklyPlans) });
        setRefreshRevision((revision) => revision + 1);
        setRemoteLoadError(false);
        return true;
      } catch (error) {
        if (api.isUnauthorizedError(error)) api.clearAuthTokens();
        else if (reportInitialFailure) setRemoteLoadError(true);
        return false;
      }
    })().finally(() => {
      refreshInFlightRef.current = null;
    });
    refreshInFlightRef.current = refresh;
    return refresh;
  }, [dispatch, initialProfile, initialSettings, monthlyGoalMutationRevisionRef, pendingDatesRef, remoteRefreshBlockedRef]);

  const refreshRemoteState = useCallback(async () => {
    await loadRemoteState(false);
  }, [loadRemoteState]);

  const retryRemoteState = useCallback(async () => {
    markHydrated(false);
    setRemoteLoadError(false);
    if (await loadRemoteState(true)) markHydrated(true);
  }, [loadRemoteState, markHydrated]);

  useEffect(() => {
    if (!sessionReady) {
      markHydrated(false);
      return;
    }
    if (!sessionActive) {
      setRemoteLoadError(false);
      markHydrated(true);
      return;
    }
    markHydrated(false);
    setRemoteLoadError(false);
    let active = true;
    void loadRemoteState(true).then((succeeded) => {
      if (active && succeeded) markHydrated(true);
    });
    return () => {
      active = false;
    };
  }, [loadRemoteState, markHydrated, sessionActive, sessionReady]);

  useEffect(() => {
    if (!sessionActive || typeof window === "undefined") return;
    const refreshIfVisible = () => {
      if (document.visibilityState === "visible") void refreshRemoteState();
    };
    const interval = window.setInterval(refreshIfVisible, 5 * 60 * 1000);
    window.addEventListener("focus", refreshIfVisible);
    document.addEventListener("visibilitychange", refreshIfVisible);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener("focus", refreshIfVisible);
      document.removeEventListener("visibilitychange", refreshIfVisible);
    };
  }, [refreshRemoteState, sessionActive]);

  return { refreshRemoteState, retryRemoteState, refreshRevision, remoteLoadError };
}
